import { useEffect, useMemo, useState } from "react";
import { Badge, Button, Card, Col, Container, Row, Spinner } from "react-bootstrap";
import { Link, useParams } from "react-router-dom";
import api, { mediaUrl } from "../../api/axios";

const statusLabels = {
  scheduled: "Programado",
  registration_open: "Asistencia abierta",
  live: "En vivo",
  active: "En vivo",
  completed: "Finalizado",
  cancelled: "Cancelado"
};

function TournamentDetail() {
  const { tournamentId } = useParams();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState("");
  const [pendingAction, setPendingAction] = useState("");
  const [pendingUnregister, setPendingUnregister] = useState(false);
  const currentUser = JSON.parse(localStorage.getItem("user") || "null");

  const loadDetail = () => {
    api
      .get(`/tournaments/${tournamentId}`)
      .then(({ data }) => setDetail(data))
      .catch(() => setDetail(null))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tournamentId]);


  const currentRound = useMemo(() => detail?.rounds?.[detail.rounds.length - 1] || null, [detail]);
  const eventModeOnly = true;
  const myMatch = useMemo(
    () => currentRound?.matches?.find(match => [match.player_one_id, match.player_two_id].includes(currentUser?.id)) || null,
    [currentRound, currentUser?.id]
  );

  useEffect(() => {
    if (!["live", "active"].includes(detail?.tournament?.status)) return undefined;
    const timer = setInterval(loadDetail, 10000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail?.tournament?.status, tournamentId]);


  const canRegisterTournament = tournament => ["scheduled", "registration_open"].includes(tournament?.status);

  const registerTournament = async () => {
    if (pendingAction) return;
    setPendingAction("register");
    setNotice("");
    try {
      const { data } = await api.post(`/tournaments/${tournamentId}/register`);
      setNotice(data.status === "waitlisted"
        ? `Te sumamos a lista de espera en la posicion ${data.waitlist_position || "-"}.`
        : "Asistencia registrada.");
      loadDetail();
    } catch (error) {
      setNotice(error?.response?.data?.error || "No se pudo confirmar asistencia al evento.");
    } finally {
      setPendingAction("");
    }
  };

  const unregisterTournament = async () => {
    if (pendingAction) return;
    setPendingAction("unregister");
    setNotice("");
    try {
      await api.delete(`/tournaments/${tournamentId}/register`);
      setNotice("Cancelaste tu asistencia al evento.");
      setPendingUnregister(false);
      loadDetail();
    } catch (error) {
      setNotice(error?.response?.data?.error || "No se pudo dar de baja.");
    } finally {
      setPendingAction("");
    }
  };

  const confirmResult = async (match, accepted) => {
    if (pendingAction) return;
    setPendingAction(match.id);
    try {
      await api.post(`/tournaments/${tournamentId}/matches/${match.id}/confirm`, { accepted });
      setNotice(accepted ? "Resultado confirmado." : "Resultado marcado como disputado.");
      loadDetail();
    } catch (error) {
      setNotice(error?.response?.data?.error || "No se pudo confirmar el resultado.");
    } finally {
      setPendingAction("");
    }
  };

  const markReady = async match => {
    if (pendingAction) return;
    setPendingAction(match.id);
    try {
      await api.post(`/tournaments/${tournamentId}/matches/${match.id}/ready`);
      setNotice("Listo confirmado.");
      loadDetail();
    } catch (error) {
      setNotice(error?.response?.data?.error || "No se pudo confirmar que estas listo.");
    } finally {
      setPendingAction("");
    }
  };

  const recordGame = async (match, winnerId) => {
    if (pendingAction) return;
    setPendingAction(match.id);
    try {
      const { data } = await api.post(`/tournaments/${tournamentId}/matches/${match.id}/games`, { winnerId });
      setNotice(data.decided
        ? `Serie ${data.player_one_score}-${data.player_two_score} enviada para confirmacion.`
        : `Partida ${data.game_number} registrada.`);
      loadDetail();
    } catch (error) {
      setNotice(error?.response?.data?.error || "No se pudo registrar la partida.");
    } finally {
      setPendingAction("");
    }
  };

  const undoLastGame = async match => {
    if (pendingAction) return;
    setPendingAction(match.id);
    try {
      await api.delete(`/tournaments/${tournamentId}/matches/${match.id}/games/last`);
      setNotice("Ultima partida corregida.");
      loadDetail();
    } catch (error) {
      setNotice(error?.response?.data?.error || "No se pudo corregir la ultima partida.");
    } finally {
      setPendingAction("");
    }
  };

  if (loading) {
    return (
      <Container className="page-band text-center py-5">
        <Spinner animation="border" />
      </Container>
    );
  }

  if (!detail) {
    return (
      <Container className="page-band">
        <Card className="cardora-card">
          <Card.Body>No encontramos este evento.</Card.Body>
        </Card>
      </Container>
    );
  }

  const { tournament, players, rounds } = detail;
  const canViewLive = Boolean(tournament.can_view_live);
  const isCompleted = tournament.status === "completed";
  const isRegistered = Boolean(tournament.viewer_is_registered);
  const isWaitlisted = Boolean(tournament.viewer_is_waitlisted);
  const isFull = tournament.capacity && Number(tournament.players_count || 0) >= Number(tournament.capacity);
  const canRegister = canRegisterTournament(tournament);

  return (
    <Container className="page-band tournament-live-page">
      <div className="tournament-live-header">
        <div className="tournament-card-store">
          {tournament.business_logo_url ? (
            <img src={mediaUrl(tournament.business_logo_url)} alt="" />
          ) : (
            <span>{tournament.business_name?.slice(0, 1) || "C"}</span>
          )}
          <div>
            <strong>{tournament.business_name}</strong>
            <small>{tournament.business_city || tournament.business_slug}</small>
          </div>
          {tournament.business_is_verified && <Badge bg="success">Verificada</Badge>}
        </div>
        <Button as={Link} to={`/store/${tournament.business_slug}`} variant="outline-dark">
          Ver tienda
        </Button>
      </div>

      <div className="community-directory-header mb-4">
        <div>
          <h1 className="section-title mb-1">{tournament.name}</h1>
          <p className="muted-copy mb-0">{tournament.description || "Evento de comunidad Cardora para encontrarte con coleccionistas y cartas que buscas."}</p>
        </div>
        <div className="tournament-detail-actions">
          <Badge bg={["live", "active"].includes(tournament.status) ? "success" : "dark"}>
            {statusLabels[tournament.status] || tournament.status}
          </Badge>
          {!localStorage.getItem("token") ? (
            <Button as={Link} to="/register" className="btn-cardora">
              Registrarme para asistir
            </Button>
          ) : isRegistered || isWaitlisted ? (
            pendingUnregister ? (
              <div className="inline-confirm-actions">
                <span>Confirmar baja?</span>
                <Button size="sm" variant="outline-danger" disabled={pendingAction === "unregister"} onClick={unregisterTournament}>
                  Darme de baja
                </Button>
                <Button size="sm" variant="outline-dark" onClick={() => setPendingUnregister(false)}>
                  Cancelar
                </Button>
              </div>
            ) : (
              <>
                <Button variant={isWaitlisted ? "outline-warning" : "outline-success"} disabled>
                  {isWaitlisted ? `Lista de espera #${tournament.viewer_waitlist_position || "-"}` : "Asistencia confirmada"}
                </Button>
                {!isCompleted && (
                  <Button variant="outline-danger" onClick={() => setPendingUnregister(true)}>
                    Darme de baja
                  </Button>
                )}
              </>
            )
          ) : canRegister ? (
            <Button className="btn-cardora" disabled={pendingAction === "register"} onClick={registerTournament}>
              {isFull ? "Lista de espera" : "Confirmar asistencia"}
            </Button>
          ) : (
            <Button variant="outline-dark" disabled>
              Asistencia cerrada
            </Button>
          )}
        </div>
      </div>
      {notice && <div className="inline-page-notice mb-3">{notice}</div>}

      <Row className="g-3 mb-4">
        <Col md={3}><div className="store-metric"><span>Asistentes</span><strong>{canViewLive ? players.filter(player => player.status !== "dropped").length : tournament.players_count || 0}</strong></div></Col>
        <Col md={3}><div className="store-metric"><span>Cupo</span><strong>{tournament.capacity || "Sin limite"}</strong></div></Col>
        <Col md={3}><div className="store-metric"><span>Fecha</span><strong>{tournament.starts_at ? new Date(tournament.starts_at).toLocaleDateString("es-ES", { day: "2-digit", month: "short" }) : "A confirmar"}</strong></div></Col>
        <Col md={3}><div className="store-metric"><span>Lista espera</span><strong>{tournament.waitlist_count || 0}</strong></div></Col>
      </Row>

      {!canViewLive && (
        <Card className="cardora-card mb-4">
          <Card.Body className="d-flex flex-wrap justify-content-between align-items-center gap-3">
            <div>
              <Card.Title className="mb-1">Conecta desde el evento</Card.Title>
              <p className="muted-copy mb-0">
                Confirma asistencia para que Cardora revise tus buscadas y te avise si alguien de la comunidad tiene cartas que te interesan.
              </p>
            </div>
            <div className="d-flex gap-2">
              <Button as={Link} to="/login" variant="outline-dark">
                Iniciar sesion
              </Button>
              <Button as={Link} to={`/store/${tournament.business_slug}`} className="btn-cardora">
                Ver comunidad
              </Button>
            </div>
          </Card.Body>
        </Card>
      )}


      {canViewLive && players.length > 0 && (
        <Card className="cardora-card mb-4">
          <Card.Body>
            <Card.Title>Asistentes de la comunidad</Card.Title>
            <p className="muted-copy">Si tienes cartas buscadas cargadas, Cardora puede avisarte cuando haya oportunidades con gente que asiste o sigue esta tienda.</p>
            <div className="tournament-player-list">
              {players.filter(player => player.status !== "dropped").slice(0, 18).map(player => (
                <Link to={`/u/${player.handle || player.user_id}`} key={player.user_id}>
                  {player.avatar_url ? <img src={mediaUrl(player.avatar_url)} alt="" /> : <span>{player.display_name?.slice(0, 1) || "C"}</span>}
                  <small>{player.display_name}</small>
                </Link>
              ))}
            </div>
          </Card.Body>
        </Card>
      )}

      {!eventModeOnly && canViewLive && myMatch && (
        <Card className="cardora-card tournament-my-match mb-4">
          <Card.Body>
            <div className="tournament-my-match-heading">
              <div>
                <span className="muted-copy">Mi enfrentamiento · Mesa {myMatch.table_number}</span>
                <h2>{myMatch.player_one_name} vs {myMatch.player_two_name || "BYE"}</h2>
              </div>
              <Badge bg={myMatch.status === "completed" ? "success" : myMatch.status === "disputed" ? "danger" : "warning"} text={["pending", "reported"].includes(myMatch.status) ? "dark" : undefined}>
                {myMatch.status === "pending"
                  ? myMatch.started_at ? "En juego" : "Esperando asistentes"
                  : myMatch.status === "reported" ? "Confirmar resultado" : myMatch.status}
              </Badge>
            </div>

            <div className="tournament-series-score">
              <div>
                <strong>{myMatch.player_one_score || 0}</strong>
                <span>{myMatch.player_one_name}</span>
                <small>{myMatch.player_one_ready_at ? "Listo" : "Sin confirmar"}</small>
              </div>
              <span>Mejor de {tournament.best_of || 3}</span>
              <div>
                <strong>{myMatch.player_two_score || 0}</strong>
                <span>{myMatch.player_two_name}</span>
                <small>{myMatch.player_two_ready_at ? "Listo" : "Sin confirmar"}</small>
              </div>
            </div>

            {!myMatch.started_at && myMatch.player_two_id && (
              <div className="d-flex justify-content-center mt-3">
                <Button
                  className="btn-cardora"
                  disabled={pendingAction === myMatch.id || (myMatch.player_one_id === currentUser?.id
                    ? Boolean(myMatch.player_one_ready_at)
                    : Boolean(myMatch.player_two_ready_at))}
                  onClick={() => markReady(myMatch)}
                >
                  {myMatch.player_one_id === currentUser?.id
                    ? myMatch.player_one_ready_at ? "Ya estas listo" : "Estoy listo"
                    : myMatch.player_two_ready_at ? "Ya estas listo" : "Estoy listo"}
                </Button>
              </div>
            )}

            {myMatch.games?.length > 0 && (
              <div className="tournament-game-history">
                {myMatch.games.map(game => (
                  <span key={game.id}>Partida {game.game_number}: {game.winner_name}</span>
                ))}
              </div>
            )}

            <div className="tournament-series-progress">
              {Array.from({ length: Number(tournament.best_of || 3) }, (_, index) => {
                const game = myMatch.games?.[index];
                return (
                  <div className={game ? "is-complete" : ""} key={index}>
                    <strong>Partida {index + 1}</strong>
                    <span>{game?.winner_name || "Pendiente"}</span>
                  </div>
                );
              })}
            </div>

            {myMatch.started_at && myMatch.status === "pending" && (
              <div className="tournament-game-actions">
                <span>¿Quién ganó la partida {myMatch.games.length + 1}?</span>
                <div>
                  <Button variant="outline-dark" disabled={pendingAction === myMatch.id} onClick={() => recordGame(myMatch, myMatch.player_one_id)}>
                    {myMatch.player_one_name}
                  </Button>
                  <Button variant="outline-dark" disabled={pendingAction === myMatch.id} onClick={() => recordGame(myMatch, myMatch.player_two_id)}>
                    {myMatch.player_two_name}
                  </Button>
                  {myMatch.games[myMatch.games.length - 1]?.reported_by === currentUser?.id && (
                    <Button variant="outline-danger" disabled={pendingAction === myMatch.id} onClick={() => undoLastGame(myMatch)}>
                      Corregir ultima
                    </Button>
                  )}
                </div>
              </div>
            )}

            {myMatch.status === "reported" && myMatch.result_reported_by !== currentUser?.id && (
              <div className="tournament-result-confirm">
                <span>Resultado informado: {myMatch.player_one_score}-{myMatch.player_two_score}</span>
                <div>
                  <Button className="btn-cardora" disabled={pendingAction === myMatch.id} onClick={() => confirmResult(myMatch, true)}>Confirmar resultado</Button>
                  <Button variant="outline-danger" disabled={pendingAction === myMatch.id} onClick={() => confirmResult(myMatch, false)}>No coincide</Button>
                </div>
              </div>
            )}
            {myMatch.status === "reported" && myMatch.result_reported_by === currentUser?.id && (
              <div className="muted-copy text-center mt-3">Esperando confirmacion de tu rival.</div>
            )}
            {myMatch.status === "disputed" && (
              <div className="tournament-dispute-notice">El resultado esta en disputa. La tienda debe revisarlo antes de cerrar la ronda.</div>
            )}
          </Card.Body>
        </Card>
      )}

      {!eventModeOnly && canViewLive && <Row className="g-4">
        <Col lg={8}>
          <Card className="cardora-card h-100">
            <Card.Body>
              <Card.Title>Mesas</Card.Title>
              {!currentRound && <div className="muted-copy">Todavia no hay rondas generadas.</div>}
              {currentRound && (
                <div className="tournament-table-grid">
                  {currentRound.matches.map(match => (
                    <div className="tournament-table-row" key={match.id}>
                      <Badge bg={match.status === "completed" ? "success" : match.status === "disputed" ? "danger" : "warning"} text={["pending", "reported"].includes(match.status) ? "dark" : undefined}>
                        Mesa {match.table_number}
                      </Badge>
                      <div>
                        <strong>{match.player_one_name}</strong>
                        <span>{match.player_two_name || "BYE"}</span>
                      </div>
                      <small>
                        {match.status === "completed"
                          ? `${match.player_one_score || 0}-${match.player_two_score || 0}`
                          : match.status === "reported" ? "Esperando confirmacion" : match.status === "disputed" ? "En disputa" : "Pendiente"}
                      </small>
                    </div>
                  ))}
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col lg={4}>
          <Card className="cardora-card h-100">
            <Card.Body>
              <Card.Title>Standings</Card.Title>
              <div className="admin-list">
                {players.filter(player => player.status !== "dropped").map((player, index) => (
                  <Link className="ranking-row" to={`/u/${player.handle || player.user_id}`} key={player.user_id}>
                    <span>{index + 1}</span>
                    {player.avatar_url ? <img src={mediaUrl(player.avatar_url)} alt="" /> : <strong>{player.display_name?.slice(0, 1)}</strong>}
                    <div>
                      <b>{player.display_name}</b>
                      <small>{player.points} pts · {player.wins}V {player.losses}D {player.draws}E</small>
                    </div>
                  </Link>
                ))}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>}

      {!eventModeOnly && canViewLive && rounds.length > 1 && (
        <Card className="cardora-card mt-4">
          <Card.Body>
            <Card.Title>Historial de rondas</Card.Title>
            <div className="tournament-history">
              {rounds.slice(1).map(round => (
                <div key={round.id}>
                  <strong>Ronda {round.round_number}</strong>
                  <span>{round.matches.length} mesas · {round.status}</span>
                </div>
              ))}
            </div>
          </Card.Body>
        </Card>
      )}
    </Container>
  );
}

export default TournamentDetail;
